/**
 * MASTER LAYOUT - Layout principal do sistema
 *
 * ESTRUTURA:
 * - Sidebar (menu lateral) com 2 comportamentos:
 *   • Desktop: Toggle entre expandido (ícones+nomes) e colapsado (só ícones)
 *   • Mobile: Abre/fecha completamente, sempre expandido quando visível
 *
 * - Navbar (barra superior) com botões de controle
 * - Área de conteúdo principal (Outlet para rotas)
 *
 * BOTÕES DE CONTROLE:
 * 1. Desktop Toggle (.sidebar-toggle): Expande/colapsa menu no desktop
 * 2. Mobile Toggle (.sidebar-mobile-toggle): Botão 3 tracinhos que abre menu mobile
 * 3. Close Button (.sidebar-close-btn): Botão X que fecha menu mobile
 *
 * ESTADOS:
 * - sidebarActive: controla expansão no desktop (true = expandido)
 * - mobileMenu: controla visibilidade no mobile (true = visível)
 *
 * CSS INJETADO:
 * O componente injeta CSS customizado para:
 * - Animações (fadeIn)
 * - Forçar exibição de nomes no menu mobile
 * - Efeitos hover nos itens do menu
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggleButton from '../helper/ThemeToggleButton';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/BootstrapConfirm';
import { useFetch } from '../hook/useFecth';

// ============================================
// INJEÇÃO DE CSS CUSTOMIZADO
// ============================================
// Adiciona estilos inline para animações e comportamento do menu mobile
const styleSheet = document.createElement('style');
styleSheet.textContent = `
    /* ==========================================
       ANIMAÇÃO FADEIN
       Usada no overlay escuro quando menu mobile abre
       ========================================== */
    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
    
    /* ==========================================
       FORÇA EXIBIÇÃO DE NOMES NO MENU MOBILE
       Garante que os textos apareçam quando .sidebar-open está ativa
       Usa !important para sobrescrever qualquer CSS existente
       ========================================== */
    .sidebar-open span,
    .sidebar-open .sidebar-menu span,
    .sidebar-open .sidebar-menu li span,
    .sidebar-open .sidebar-menu a span,
    .sidebar-open .sidebar-submenu span {
        display: inline-block !important;
        opacity: 1 !important;
        visibility: visible !important;
        white-space: nowrap !important;
        max-width: none !important;
        width: auto !important;
        overflow: visible !important;
    }
    
    .sidebar-open .menu-icon {
        margin-right: 10px !important;
        display: inline-flex !important;
    }
    
    .sidebar-open .sidebar-menu a,
    .sidebar-open .sidebar-menu li a {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        overflow: visible !important;
    }
    
    /* ==========================================
       LARGURA FIXA E TRANSIÇÕES NO MENU MOBILE
       Garante que o sidebar tenha espaço suficiente para nomes
       E adiciona transições suaves em todos os estados
       
       CONTROLE DE VELOCIDADE DA ABERTURA HORIZONTAL:
       - transform: controla o DESLIZAMENTO (esquerda → direita)
       - width: controla a EXPANSÃO da largura
       
       Valores de exemplo:
       - 0.3s = rápido
       - 1s = normal (atual)
       - 2s = devagar
       ========================================== */


    /*  HOVER DO MENU */
    .sidebar {
        transition: transform 0.3s ease-in-out, width 0.6s ease-in-out !important;
    }
    
    .sidebar-open {
        width: 260px !important;
        min-width: 260px !important;
        transition: width 0.3s ease-in-out !important;
    }
    
    /* Transições suaves para textos e ícones dentro do sidebar */
    .sidebar .sidebar-menu span,
    .sidebar .sidebar-menu a {
        transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out !important;
    }
    
    /* ==========================================
       EFEITOS HOVER E ESTADOS ATIVOS
       Efeitos visuais ao passar o mouse e indicação de página ativa
       Cor azul padrão: #3b82f6 (blue-500)
       ========================================== */
    .sidebar-open .sidebar-menu li a:hover {
        background-color: rgba(59, 130, 246, 0.1) !important;
        color: #3b82f6 !important;
        border-radius: 8px !important;
        transition: all 0.3s ease !important;
    }
    
    .sidebar-open .sidebar-menu li a:hover span {
        color: #3b82f6 !important;
    }
    
    .sidebar-open .sidebar-menu li a:hover .menu-icon {
        color: #3b82f6 !important;
    }
    
    /* Item ativo */
    .sidebar-open .sidebar-menu li a.active-page {
        background-color: rgba(59, 130, 246, 0.15) !important;
        color: #3b82f6 !important;
        font-weight: 600 !important;
    }
    
    /* Submenu hover */
    .sidebar-open .sidebar-submenu li a:hover {
        background-color: rgba(59, 130, 246, 0.08) !important;
        color: #3b82f6 !important;
        padding-left: 12px !important;
        transition: all 0.3s ease !important;
    }
`;
if (!document.head.querySelector('style[data-sidebar-animations]')) {
    styleSheet.setAttribute('data-sidebar-animations', 'true');
    document.head.appendChild(styleSheet);
}

const MasterLayout = () => {
    const { logout } = useAuth();
    const { data: user } = useFetch(`/profile`);

    // ESTADOS DO MENU
    // sidebarActive: controla expansão/colapso do menu no DESKTOP (ícones vs ícones+nomes)
    let [sidebarActive, seSidebarActive] = useState(true);

    // mobileMenu: controla abertura/fechamento do menu no MOBILE (visível vs oculto)
    let [mobileMenu, setMobileMenu] = useState(false);

    const location = useLocation(); // Hook to get the current route
    const navigate = useNavigate();
    const confirm = useConfirm();

    // EFEITO: Gerencia abertura/fechamento de dropdowns (submenus) no menu lateral
    useEffect(() => {
        const handleDropdownClick = event => {
            event.preventDefault();
            const clickedLink = event.currentTarget;
            const clickedDropdown = clickedLink.closest('.dropdown');

            if (!clickedDropdown) return;

            const isActive = clickedDropdown.classList.contains('open');

            // Fecha todos os dropdowns antes de abrir o clicado (comportamento "accordion")
            const allDropdowns = document.querySelectorAll('.sidebar-menu .dropdown');
            allDropdowns.forEach(dropdown => {
                dropdown.classList.remove('open');
                const submenu = dropdown.querySelector('.sidebar-submenu');
                if (submenu) {
                    submenu.style.maxHeight = '0px'; // Colapsa submenu
                }
            });

            // Abre o dropdown clicado (se não estava aberto)
            if (!isActive) {
                clickedDropdown.classList.add('open');
                const submenu = clickedDropdown.querySelector('.sidebar-submenu');
                if (submenu) {
                    submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expande submenu
                }
            }
        };

        // Attach click event listeners to all dropdown triggers
        const dropdownTriggers = document.querySelectorAll('.sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link');

        dropdownTriggers.forEach(trigger => {
            trigger.addEventListener('click', handleDropdownClick);
        });

        const openActiveDropdown = () => {
            const allDropdowns = document.querySelectorAll('.sidebar-menu .dropdown');
            allDropdowns.forEach(dropdown => {
                const submenuLinks = dropdown.querySelectorAll('.sidebar-submenu li a');
                submenuLinks.forEach(link => {
                    if (link.getAttribute('href') === location.pathname || link.getAttribute('to') === location.pathname) {
                        dropdown.classList.add('open');
                        const submenu = dropdown.querySelector('.sidebar-submenu');
                        if (submenu) {
                            submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expand submenu
                        }
                    }
                });
            });
        };

        // Open the submenu that contains the active route
        openActiveDropdown();

        // Cleanup event listeners on unmount
        return () => {
            dropdownTriggers.forEach(trigger => {
                trigger.removeEventListener('click', handleDropdownClick);
            });
        };
    }, [location.pathname]);

    // EFEITO: Fecha o menu mobile automaticamente ao navegar para outra página
    useEffect(() => {
        setMobileMenu(false);
    }, [location.pathname]);

    // EFEITO: Força a expansão do sidebar quando o menu mobile abre
    // Garante que os nomes dos itens fiquem visíveis (não só ícones)
    useEffect(() => {
        if (mobileMenu) {
            const sidebar = document.querySelector('.sidebar');
            if (sidebar) {
                sidebar.classList.add('active');
            }
        }
    }, [mobileMenu]);

    // FUNÇÃO: Controla expansão/colapso do menu no DESKTOP
    // Alterna entre mostrar só ícones ou ícones + nomes
    let sidebarControl = () => {
        seSidebarActive(!sidebarActive);
    };

    // FUNÇÃO: Controla abertura/fechamento do menu no MOBILE
    // Alterna entre menu visível ou oculto
    let mobileMenuControl = () => {
        setMobileMenu(!mobileMenu);
    };

    async function Logout() {
        await confirm({ message: 'Você está saindo do sistema?' });
        // logout já chama /auth/logout no backend e navega para "/"
        await logout();
    }
    // if (Boolean(user) && user.profileType.includes('broker') && !Boolean(user?.registerFullAt)) {
    //     return <Navigate to="validar-conta" replace={true} />;
    // }

    // Controles de permissão/visibilidade de menus
    const isSuper = user?.super;
    const isAdmin = user?.funcao === 'ADMIN';
    const isFuncionario = user?.funcao === 'FUNCIONARIO';

    /* ============================================
       FLUXO DE FUNCIONAMENTO DO MENU MOBILE:
       
       1. Usuário clica no botão 3 tracinhos (.sidebar-mobile-toggle)
       2. mobileMenuControl é chamado
       3. Estado mobileMenu muda para true
       4. Overlay escuro aparece com fadeIn
       5. Sidebar recebe classes 'active sidebar-open'
       6. useEffect força sidebar.classList.add('active')
       7. CSS injetado força exibição dos nomes
       8. Menu aparece expandido com ícones + nomes
       
       FECHAR:
       - Clicar no X (.sidebar-close-btn) OU
       - Clicar no overlay escuro OU
       - Navegar para outra página (useEffect)
       ============================================ */

    return (
        <>
            {/* 
                OVERLAY ESCURO (Mobile)
                Aparece quando o menu mobile está aberto
                Clicar nele fecha o menu (chama mobileMenuControl)
                Animação fadeIn de 1s para teste
            */}
            {/* {mobileMenu && (
                <div 
                    onClick={mobileMenuControl}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 998,
                        animation: 'fadeIn 1s ease-in-out'
                    }}
                />
            )} */}

            <section className={mobileMenu ? 'overlay active' : 'overlay'}>
                {/* 
                    MENU LATERAL (SIDEBAR)
                    Classes condicionais:
                    - Mobile: 'active sidebar-open' (sempre expandido com nomes)
                    - Desktop: 'active' (expandido) ou sem 'active' (colapsado/só ícones)
                    
                    Largura forçada no mobile: 260px para mostrar nomes completos
                */}
                <aside className={`sidebar ${mobileMenu ? 'active sidebar-open' : sidebarActive ? 'active' : ''}`}>
                    {/* 
                        BOTÃO X (Fechar menu mobile)
                        Visível apenas no mobile quando o menu está aberto
                        Chama mobileMenuControl para fechar o menu
                        Classe CSS: .sidebar-close-btn
                    */}
                    <button onClick={mobileMenuControl} type="button" className="sidebar-close-btn">
                        <Icon icon="radix-icons:cross-2" />
                    </button>
                    <div>
                        <Link to="/" className="sidebar-logo">
                            <img src="/assets/images/logo.png" alt="site logo" className="light-logo" />
                            <img src="/assets/images/logo-light.png" alt="site logo" className="dark-logo" />
                            <img src="/assets/images/logo-icon.png" alt="site logo" className="logo-icon" />
                        </Link>
                    </div>
                    <div className="sidebar-menu-area">
                        <ul className="sidebar-menu" id="sidebar-menu">
                            {isFuncionario ? (
                                // Menu apenas para FUNCIONARIO - só Pedidos
                                <li className="dropdown">
                                    <Link to="pedidos">
                                        <Icon icon="iconoir:cart" className="menu-icon" />
                                        <span>Pedidos</span>
                                    </Link>
                                    <ul className="sidebar-submenu">
                                            <li>
                                                <NavLink to="pedidos" end className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-primary-600 w-auto circle-icon ri-circle-fill" /> Todos os Pedidos
                                                </NavLink>
                                            </li>
                                            {/* <li>
                                                <NavLink to="pedidos/add" className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-info-main w-auto circle-icon ri-circle-fill" /> Cadastrar Pedido
                                                </NavLink>
                                            </li> */}
                                        </ul>
                                </li>
                            ) : (
                                // Menu completo para ADMIN
                                <>
                                    <li>
                                        <Link to="/dashboard">
                                            <Icon icon="solar:chart-2-outline" className="menu-icon" />
                                            <span>Dashboard</span>
                                        </Link>
                                    </li>
                                
                                    <li className="dropdown">
                                        <Link to="#">
                                            <Icon icon="solar:users-group-rounded-outline" className="menu-icon" />
                                            <span>Usuários</span>
                                        </Link>

                                        <ul className="sidebar-submenu">
                                            <li>
                                                <NavLink to="usuarios" end className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-primary-600 w-auto circle-icon ri-circle-fill" /> Todos os usuários
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink to="usuarios/add" className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-info-main w-auto circle-icon ri-circle-fill" /> Cadastrar usuário
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </li>

                                    <li className="dropdown">
                                        <Link to="#">
                                            <Icon icon="solar:user-id-outline" className="menu-icon" />
                                            <span>Clientes</span>
                                        </Link>
                                        <ul className="sidebar-submenu">
                                            <li>
                                                <NavLink to="clientes" end className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-primary-600 w-auto circle-icon ri-circle-fill" /> Todos os Clientes
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink to="clientes/add" className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-info-main w-auto circle-icon ri-circle-fill" /> Cadastrar Cliente
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </li>

                                    <li className="dropdown">
                                        <Link to="#">
                                            <Icon icon="solar:box-outline" className="menu-icon" />
                                            <span>Produtos</span>
                                        </Link>
                                        <ul className="sidebar-submenu">
                                            <li>
                                                <NavLink to="produtos" end className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-primary-600 w-auto circle-icon ri-circle-fill" /> Todos os Produtos
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink to="produtos/add" className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-info-main w-auto circle-icon ri-circle-fill" /> Cadastrar produto
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </li>

                                    <li className="dropdown">
                                        <Link to="#">
                                            <Icon icon="solar:cart-large-2-outline" className="menu-icon" />
                                            <span>Pedidos</span>
                                        </Link>
                                        <ul className="sidebar-submenu">
                                            <li>
                                                <NavLink to="pedidos" end className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-primary-600 w-auto circle-icon ri-circle-fill" /> Todos os Pedidos
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink to="pedidos/add" className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-info-main w-auto circle-icon ri-circle-fill" /> Cadastrar Pedido
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </li>

                                    <li className="dropdown">
                                        <Link to="#">
                                            <Icon icon="solar:chart-square-outline" className="menu-icon" />
                                            <span>Relatórios</span>
                                        </Link>
                                        <ul className="sidebar-submenu">
                                            <li>
                                                <NavLink to="relatorio" end className={navData => (navData.isActive ? 'active-page' : '')}>
                                                    <i className="text-primary-600 w-auto circle-icon ri-circle-fill" /> Relatorios
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                </aside>

                {/* ÁREA PRINCIPAL DO CONTEÚDO */}
                <main className={sidebarActive ? 'dashboard-main active' : 'dashboard-main'}>
                    {/* NAVBAR/HEADER */}
                    <div className="navbar-header">
                        <div className="row   align-items-center justify-content-between">
                            <div className="col-auto">
                                <div className="d-flex flex-wrap align-items-center gap-4">
                                    {/* 
                                        BOTÃO TOGGLE DESKTOP
                                        Visível apenas em telas maiores
                                        Expande/colapsa o menu lateral (ícones vs ícones+nomes)
                                        Chama sidebarControl
                                        Classe CSS: .sidebar-toggle
                                    */}
                                    <button type="button" className="sidebar-toggle" onClick={sidebarControl}>
                                        {/* {sidebarActive ? (
                                        <Icon icon="iconoir:arrow-right" className="text-2xl icon non-active" />
                                    ) : (
                                        <Icon icon="heroicons:bars-3-solid" className="text-2xl icon non-active" />
                                    )} */}
                                    </button>

                                    {/* 
                                        BOTÃO 3 TRACINHOS (Mobile)
                                        Visível apenas em telas pequenas (mobile)
                                        Abre/fecha o menu lateral mobile
                                        Chama mobileMenuControl
                                        Classe CSS: .sidebar-mobile-toggle
                                        Ícone: heroicons:bars-3-solid (3 tracinhos horizontais)
                                        
                                        IMPORTANTE: Esse é o botão que quando clicado,
                                        faz aparecer o menu lateral com o botão X (sidebar-close-btn)
                                    */}
                                    <button onClick={mobileMenuControl} type="button" className="sidebar-mobile-toggle">
                                        <Icon icon="heroicons:bars-3-solid" className="icon" />
                                    </button>
                                    {/* <form className="navbar-search">
                                    <input type="text" name="search" placeholder="Search" />
                                    <Icon icon="ion:search-outline" className="icon" />
                                </form> */}
                                </div>
                            </div>
                            <div className="col-auto">
                                <div className="d-flex flex-wrap align-items-center gap-3">

                                    <ThemeToggleButton />
                                    {/* Notification dropdown end */}
                                    <div className="dropdown">
                                        <button
                                            className="d-flex align-items-center bg-neutral-200 h-40-px justify-content-center rounded-circle w-40-px has-indicator"
                                            type="button"
                                            data-bs-toggle="dropdown"
                                        >
                                            <Icon icon="iconoir:user" className="text-primary-light text-xl" />
                                        </button>
                                        <div className="dropdown-menu dropdown-menu-sm to-top">
                                            <div className="d-flex align-items-center bg-primary-50 justify-content-between gap-2 mb-16 px-16 py-12 radius-8">
                                                <div>
                                                    <h6 className="text-lg text-primary-light fw-semibold mb-2">{user?.name}</h6>
                                                    <span className="text-secondary-light text-sm fw-medium">{user?.email}</span>
                                                </div>
                                                <button type="button" className="hover-text-danger">
                                                    <Icon icon="radix-icons:cross-1" className="text-xl icon" />
                                                </button>
                                            </div>
                                            <ul className="to-top-list">
                                                <li>
                                                    <Link
                                                        className="d-flex dropdown-item align-items-center text-black gap-3 hover-bg-transparent hover-text-primary px-0 py-8"
                                                        to="perfil"
                                                    >
                                                        <Icon icon="solar:user-linear" className="text-xl icon" /> Meu perfil
                                                    </Link>
                                                </li>
                                                {/* <li>
                                                <Link
                                                    className="d-flex dropdown-item align-items-center text-black gap-3 hover-bg-transparent hover-text-primary px-0 py-8"
                                                    to="/email"
                                                >
                                                    <Icon icon="tabler:message-check" className="text-xl icon" /> 
                                                </Link>
                                            </li> */}
                                                {/* <li>
                                                <Link
                                                    className="d-flex dropdown-item align-items-center text-black gap-3 hover-bg-transparent hover-text-primary px-0 py-8"
                                                    to="/company"
                                                >
                                                    <Icon icon="solar:wallet-2-broken" className="text-xl icon" />
                                                    Conta Recebedora
                                                </Link>
                                            </li> */}
                                                <li>
                                                    <Link
                                                        className="d-flex dropdown-item align-items-center text-black gap-3 hover-bg-transparent hover-text-danger px-0 py-8"
                                                        onClick={Logout}
                                                    >
                                                        <Icon icon="lucide:power" className="text-xl icon" /> Sair
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    {/* Profile dropdown end */}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* dashboard-main-body */}
                    <div className="dashboard-main-body">
                        <Outlet />
                    </div>

                    {/* Footer section */}
                    <footer className="d-footer shadow">
                        <div className="row align-items-center justify-content-between">
                            <div className="col-auto">
                                <p className="mb-0">©{new Date().getFullYear()} Katia Karine</p>
                            </div>
                            {/* <div className="col-auto">
                            <p className="mb-0">
                                Feito com <span className="text-primary-600">wowtheme7</span>
                            </p>
                        </div> */}
                        </div>
                    </footer>
                </main>
            </section>
        </>
    );
};

export default MasterLayout;
